import api from '@/api';
import type { Me } from '@/api/queries/me/types/me';
import useMeQuery from '@/api/queries/me/useMeQuery';
import type { User, UserData } from '@/api/queries/user/types/user';
import { customQueryClient } from '@/config/query-client';
import { useMutation, useQueryClient } from '@tanstack/vue-query';

type UserDataReturn = {
  uuid: string;
  slug_full_name: string;
  sourcing: null | string;
  location: {
    gps: {
      lat: number;
      long: number;
    };
    city: string;
    country: string;
    placeId: string;
  };
  profile_visibility: number;
  job_title_id: number;
  is_job_v2: null | boolean;
  job_category_id: null | number;
  career_level_id: null | number;
  planhat_id: null | number;
  industry_id: number; // @todo as back to return the industry object ?
  elastic_sync_at: string;
  updated_at: string;
  is_sourceable: boolean;
  user_sourceable_choice: null | boolean;
  sourceable_origin: string;
  sourceable_updated_at: string;
};

/**
 * use to update the user_data object of the user
 * @param uuid
 * @param userData
 */
function updateUserData(uuid: string, userData: Partial<UserData>) {
  return api<UserDataReturn>({
    method: 'patch',
    url: `/user/${uuid}/data`,
    data: {
      ...userData,
    },
  });
}

export function useUserDataMutation() {
  const queryClient = useQueryClient();
  const { auth } = useMeQuery();

  if (!auth.value) {
    throw new Error('Auth uuid is not defined');
  }

  const uuid = auth.value.uuid;

  return useMutation(
    {
      mutationFn: (user: Partial<UserData>) => updateUserData(uuid, user),

      // mutate the cache on success for a better UX
      onSuccess: data => {
        const industryId = data?.industry_id;

        queryClient.setQueryData(['user', uuid], (old: User) => {
          if (!data) return old;

          return {
            ...old,
            user_data: {
              ...old.user_data,
              ...data,
              industry: {
                id: industryId ? industryId : old.user_data.industry?.id,
              },
            },
          };
        });

        queryClient.setQueryData(['me'], (old: Me) => {
          if (!data) return old;

          return {
            ...old,
            user: {
              ...old.user,
              user_data: {
                ...old.user.user_data,
                ...data,
                industry: {
                  id: industryId ? industryId : old.user.user_data.industry?.id,
                },
              },
            },
          };
        });
      },
      // keep the cache in sync with the server
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: ['user', uuid] });
        queryClient.invalidateQueries({ queryKey: ['me'] });
      },
    },
    customQueryClient,
  );
}
